import { useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash";
import {
  ColumnId,
  FilterControl,
  SortDirection,
  TableSchema,
  isSortDirection,
} from "../types";

type UseTableFiltersParams = {
  schema: TableSchema;
  singleSort?: boolean;
  onFilterApply?: (data: {
    columnId: ColumnId;
    sortDir: SortDirection | null;
    filterData: FilterControl["filters"][ColumnId]["data"] | null;
    filterFieldId?: string;
    sortFieldId?: string;
    columnSchema: TableSchema[ColumnId];
  }) => void;
};

export function useTableFilters({
  schema,
  singleSort,
  onFilterApply,
}: UseTableFiltersParams): FilterControl {
  if (!schema) {
    throw new Error("schema is required for useTableFilters");
  }
  const [filters, setFilters] = useState<FilterControl["filters"]>({});
  const [sortData, setSortData] = useState<FilterControl["sortData"]>({});
  const onFilterApplyRef = useRef<UseTableFiltersParams["onFilterApply"]>();
  onFilterApplyRef.current = onFilterApply;

  const controller = useMemo(() => {
    function applyFilter(
      columnId: ColumnId,
      data: { filterData?: any[]; sortDir: string | null },
    ) {
      const { sortDir, filterData } = data;
      setSortData((currentVal) => {
        const newVal = singleSort && !!sortDir ? {} : { ...currentVal };
        if (isSortDirection(sortDir) && schema[columnId].sort) {
          newVal[columnId] = {
            fieldId: schema[columnId].sort.fieldId,
            direction: sortDir,
          };
        } else {
          delete newVal[columnId];
        }
        return newVal;
      });

      setFilters((currentVal) => {
        const newVal = { ...currentVal };
        if (isEmpty(filterData)) {
          delete newVal[columnId];
        } else if (schema[columnId].filter) {
          newVal[columnId] = {
            fieldId: schema[columnId].filter.fieldId,
            data: filterData || [],
          };
        }
        return newVal;
      });

      const colSort = schema[columnId].sort;
      const colFilter = schema[columnId].filter;
      onFilterApplyRef.current?.({
        columnId,
        sortDir: isSortDirection(sortDir) ? sortDir : null,
        filterData: isEmpty(filterData) ? null : filterData || null,
        filterFieldId: colFilter ? colFilter.fieldId : undefined,
        sortFieldId: colSort ? colSort.fieldId : undefined,
        columnSchema: schema[columnId],
      });
    }

    return {
      filters,
      sortData,
      setFilters,
      setSortData,
      applyFilter,
    };
  }, [filters, sortData, schema, singleSort]);
  return controller;
}
